import React, { useEffect, useMemo, useState, useCallback } from "react";
import axios from "../../api";
import {
  Typography,
  Container,
  Paper,
  Box,
  Button,
  TextField,
  Divider,
  CircularProgress,
  Switch,
  Chip,
  Avatar,
  Alert,
  Stack,
  InputAdornment,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItemButton,
  ListItemText,
  ListSubheader,
  Tabs,
  Tab,
  Tooltip,
  alpha,
} from "@mui/material";
import {
  Business,
  Tune,
  Email,
  PictureAsPdf,
  ListAlt,
  VerifiedUser,
  NotificationsActive,
  Schedule,
  Save as SaveIcon,
  CloudUpload,
  Image as ImageIcon,
  RestartAlt,
  Visibility,
  Add,
  Security,
  Place,
  Inventory2,
  Search,
  ContentCopy,
  CheckCircleOutline,
  HighlightOff,
} from "@mui/icons-material";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import AdminLayout from "../AdminLayout";

const get = (obj, path, fallback = "") =>
  path.split(".").reduce((acc, k) => (acc == null ? acc : acc[k]), obj) ?? fallback;

const TABS = [
  {
    key: "company",
    label: "Company",
    icon: <Business fontSize="small" />,
    desc: "Branding shown across the portal, emails and PDFs.",
    group: "Organization",
  },
  {
    key: "app",
    label: "Application",
    icon: <Tune fontSize="small" />,
    desc: "App name and the base URL used in activation and acknowledgment links.",
    group: "Organization",
  },
  {
    key: "auth",
    label: "Sign-in (SSO)",
    icon: <Security fontSize="small" />,
    desc: "Microsoft and Google login toggles (requires env secrets).",
    group: "Organization",
  },
  {
    key: "emails",
    label: "Emails",
    icon: <Email fontSize="small" />,
    desc: "Templates for invites, reminders and confirmation emails.",
    group: "Communications",
  },
  {
    key: "reminders",
    label: "Reminders",
    icon: <Schedule fontSize="small" />,
    desc: "Automatic follow-ups for pending public undertaking invites.",
    group: "Communications",
  },
  {
    key: "notifications",
    label: "Notifications",
    icon: <NotificationsActive fontSize="small" />,
    desc: "Admin alerts when employees submit undertakings.",
    group: "Communications",
  },
  {
    key: "verification",
    label: "Verification",
    icon: <VerifiedUser fontSize="small" />,
    desc: "Periodic asset-verification schedule and campaign email.",
    group: "Communications",
  },
  {
    key: "documents",
    label: "Documents & Terms",
    icon: <PictureAsPdf fontSize="small" />,
    desc: "PDF header/footer and the undertaking terms employees accept.",
    group: "Content",
  },
  {
    key: "form",
    label: "Form Options",
    icon: <ListAlt fontSize="small" />,
    desc: "Dropdown choices shown on the undertaking form.",
    group: "Content",
  },
  {
    key: "locations",
    label: "Locations",
    icon: <Place fontSize="small" />,
    desc: "Site → Building → Floor catalog for inventory dropdowns.",
    group: "Content",
  },
  {
    key: "assets",
    label: "Inventory",
    icon: <Inventory2 fontSize="small" />,
    desc: "Recycle bin retention for soft-removed assets.",
    group: "Content",
  },
];

const NAV_GROUPS = ["Organization", "Communications", "Content"];
const TAB_KEYS = new Set(TABS.map((t) => t.key));

const EMAIL_TEMPLATES = [
  {
    key: "welcome",
    label: "Welcome / Activation",
    hint: "Sent when a user is created — set-password link plus what they need to do.",
  },
  {
    key: "instructions",
    label: "Instructions",
    hint: "Step-by-step: sign in, open My Devices, acknowledge.",
  },
  {
    key: "invite",
    label: "Invite",
    hint: "Sent when inviting someone to fill the public undertaking form.",
  },
  {
    key: "reminder",
    label: "Reminder",
    hint: "Sent by the invite reminder cron to non-responders.",
  },
  {
    key: "submission",
    label: "Submission",
    hint: "Confirmation after submit, with PDF attached.",
  },
  {
    key: "updated",
    label: "Updated",
    hint: "Sent when an admin updates a submitted undertaking.",
  },
];

const panelSx = {
  p: 2,
  borderRadius: 2,
  border: "1px solid",
  borderColor: "divider",
  bgcolor: "background.paper",
};

const FormGrid = ({ children }) => (
  <Box
    sx={{
      display: "grid",
      gap: 2.5,
      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
      alignItems: "start",
    }}
  >
    {children}
  </Box>
);

const Full = ({ children, sx }) => (
  <Box sx={{ gridColumn: "1 / -1", ...sx }}>{children}</Box>
);

const Section = ({ title, description, action, children, sx }) => (
  <Box
    sx={{
      gridColumn: "1 / -1",
      pt: 0.5,
      ...sx,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 1.5,
        flexWrap: "wrap",
        mb: children ? 1.5 : 0,
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        {title && (
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {title}
          </Typography>
        )}
        {description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25 }}>
            {description}
          </Typography>
        )}
      </Box>
      {action}
    </Box>
    {children}
  </Box>
);

function SettingsPage({ onLogout }) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [settings, setSettings] = useState(null);
  const [baseline, setBaseline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const initialTab = TAB_KEYS.has(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "company";
  const [tabKey, setTabKey] = useState(initialTab);
  const [navQuery, setNavQuery] = useState("");
  const [chipDraft, setChipDraft] = useState({});
  const [emailTemplateKey, setEmailTemplateKey] = useState("welcome");
  const [previewEmail, setPreviewEmail] = useState(null);
  const [termsDraft, setTermsDraft] = useState("");
  const [testEmailTo, setTestEmailTo] = useState("");
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [ssoStatus, setSsoStatus] = useState(null);
  const [warrantyRunning, setWarrantyRunning] = useState(false);
  const [mailReadiness, setMailReadiness] = useState(null);

  const fetchMailReadiness = useCallback(async () => {
    try {
      const res = await axios.get("/settings/mail-readiness");
      setMailReadiness(res.data.result || null);
    } catch {
      setMailReadiness(null);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
    fetchMailReadiness();
    const me = JSON.parse(localStorage.getItem("adminUser") || "null");
    if (me?.email) setTestEmailTo(me.email);
    axios
      .get("/auth/providers")
      .then((r) => setSsoStatus(r.data.result || null))
      .catch(() => setSsoStatus(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const fromUrl = searchParams.get("tab");
    if (TAB_KEYS.has(fromUrl) && fromUrl !== tabKey) {
      setTabKey(fromUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (tabKey === "app" || tabKey === "emails") {
      fetchMailReadiness();
    }
  }, [tabKey, fetchMailReadiness]);

  const mailReadinessAlert = mailReadiness ? (
    <Full>
      <Alert
        severity={mailReadiness.activationLinksWillWork ? "success" : "warning"}
        sx={{ py: 0.75 }}
      >
        {mailReadiness.activationLinksWillWork
          ? "Invite and activation emails should work — Frontend URL and mail transport are configured."
          : "Email links may not work until you fix:"}
        {!mailReadiness.activationLinksWillWork && (
          <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2.5 }}>
            {!mailReadiness.frontendUrlConfigured && (
              <li>
                Frontend URL (Application tab) — currently{" "}
                {mailReadiness.frontendUrl ? mailReadiness.frontendUrl : "not set"}
              </li>
            )}
            {!mailReadiness.mailTransportOk && (
              <li>
                Mail transport ({mailReadiness.mailMode || "unknown"}
                {mailReadiness.mailError ? `: ${mailReadiness.mailError}` : ""})
              </li>
            )}
          </Box>
        )}
      </Alert>
    </Full>
  ) : null;

  const dirty = useMemo(() => {
    if (!settings || !baseline) return false;
    return JSON.stringify(settings) !== JSON.stringify(baseline);
  }, [settings, baseline]);

  const filteredTabs = useMemo(() => {
    const q = navQuery.trim().toLowerCase();
    if (!q) return TABS;
    return TABS.filter(
      (t) =>
        t.label.toLowerCase().includes(q) ||
        t.desc.toLowerCase().includes(q) ||
        t.group.toLowerCase().includes(q)
    );
  }, [navQuery]);

  const selectTab = (nextKey) => {
    if (nextKey === tabKey) return;
    if (
      dirty &&
      !window.confirm(
        "You have unsaved changes on this section. Switch anyway? Unsaved edits stay in memory until you Save or Discard."
      )
    ) {
      return;
    }
    setTabKey(nextKey);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", nextKey);
        return next;
      },
      { replace: true }
    );
  };

  const fetchSettings = async () => {
    try {
      const resp = await axios.get("/settings");
      const data = resp.data.result;
      setSettings(data);
      setBaseline(structuredClone(data));
      setTermsDraft((data.terms || []).join("\n"));
    } catch (err) {
      console.error("Failed to load settings", err);
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const sendTestEmail = async () => {
    setTestEmailSending(true);
    try {
      const res = await axios.post("/settings/test-email", {
        to: testEmailTo.trim(),
      });
      toast.success(res.data.message || "Test email sent.");
    } catch (err) {
      toast.error(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to send test email"
      );
    } finally {
      setTestEmailSending(false);
    }
  };

  const commitTermsDraft = (raw) => {
    setTermsDraft(raw);
    setField(
      "terms",
      raw
        .split("\n")
        .map((s) => s.trim())
        .filter(Boolean)
    );
  };

  const setField = (path, value) => {
    setSettings((prev) => {
      const next = structuredClone(prev);
      const keys = path.split(".");
      let cur = next;
      for (let i = 0; i < keys.length - 1; i++) {
        if (cur[keys[i]] == null) cur[keys[i]] = {};
        cur = cur[keys[i]];
      }
      cur[keys[keys.length - 1]] = value;
      return next;
    });
  };

  const setNumberField = (path, raw) => {
    if (raw === "" || raw == null) {
      setField(path, "");
      return;
    }
    const n = Number(raw);
    setField(path, Number.isNaN(n) ? raw : n);
  };

  const discardChanges = () => {
    if (!baseline) return;
    setSettings(structuredClone(baseline));
    setTermsDraft((baseline.terms || []).join("\n"));
    toast.info("Changes discarded");
  };

  const loadRecommendedTerms = async () => {
    if (
      (get(settings, "terms", []) || []).length &&
      !window.confirm(
        "Replace the current terms with the recommended set? Your existing terms will be overwritten (you can still edit before saving)."
      )
    ) {
      return;
    }
    try {
      const resp = await axios.get("/settings/default-terms");
      const next = resp.data.result || [];
      setField("terms", next);
      setTermsDraft(next.join("\n"));
      toast.info("Recommended terms loaded. Review, then click Save.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not load recommended terms");
    }
  };

  const loadRecommendedEmailTemplate = async (key) => {
    const cur = get(settings, `email.${key}`, {}) || {};
    if (
      (cur.subject || cur.html) &&
      !window.confirm(
        `Replace the current "${key}" template with the recommended one? (You can still edit before saving.)`
      )
    ) {
      return;
    }
    try {
      const resp = await axios.get(`/settings/default-email-template/${key}`);
      setField(`email.${key}`, resp.data.result || { subject: "", html: "" });
      toast.info("Recommended template loaded. Review, then click Save.");
    } catch (err) {
      toast.error(err.response?.data?.message || "Could not load recommended template");
    }
  };

  const loadRecommendedVerificationTemplate = async () => {
    const cur = get(settings, "verification.template", {}) || {};
    if (
      (cur.subject || cur.html) &&
      !window.confirm(
        "Replace the current verification email subject and body with the recommended template? (You can still edit before saving.)"
      )
    ) {
      return;
    }
    try {
      const resp = await axios.get("/settings/default-verification-template");
      setField("verification.template", resp.data.result || { subject: "", html: "" });
      toast.info("Recommended template loaded. Review, then click Save.");
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not load recommended template");
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const resp = await axios.put("/settings", settings);
      const data = resp.data.result;
      setSettings(data);
      setBaseline(structuredClone(data));
      setTermsDraft((data.terms || []).join("\n"));
      toast.success("Settings saved successfully");
    } catch (err) {
      console.error("Failed to save settings", err);
      toast.error(err.response?.data?.error || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const form = new FormData();
    form.append("logo", file);
    try {
      const resp = await axios.post("/settings/logo", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const data = resp.data.result;
      setSettings(data);
      setBaseline(structuredClone(data));
      setTermsDraft((data.terms || []).join("\n"));
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.error || "Logo upload failed");
    } finally {
      e.target.value = "";
    }
  };

  const handleLogout = () => {
    if (onLogout) return onLogout();
    localStorage.removeItem("isAdminLoggedIn");
    localStorage.removeItem("adminUser");
    navigate("/login");
  };

  const addChip = (path) => {
    const draft = (chipDraft[path] || "").trim();
    if (!draft) return;
    const current = get(settings, path, []) || [];
    if (current.some((v) => String(v).toLowerCase() === draft.toLowerCase())) {
      toast.warning("Already in the list");
      return;
    }
    setField(path, [...current, draft]);
    setChipDraft((prev) => ({ ...prev, [path]: "" }));
  };

  const removeChip = (path, value) => {
    const current = get(settings, path, []) || [];
    setField(
      path,
      current.filter((v) => v !== value)
    );
  };

  if (loading || !settings) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  const activeTab = TABS.find((t) => t.key === tabKey) || TABS[0];
  const selectedEmailTpl =
    EMAIL_TEMPLATES.find((t) => t.key === emailTemplateKey) || EMAIL_TEMPLATES[0];

  const field = (label, path, opts = {}) => (
    <Box sx={{ gridColumn: opts.full ? "1 / -1" : "auto", minWidth: 0 }}>
      <TextField
        label={label}
        value={get(settings, path)}
        onChange={(e) =>
          opts.number
            ? setNumberField(path, e.target.value)
            : setField(path, e.target.value)
        }
        fullWidth
        size="small"
        type={opts.number ? "number" : "text"}
        multiline={opts.multiline || false}
        minRows={opts.minRows}
        placeholder={opts.placeholder}
        helperText={opts.helper}
        InputProps={opts.InputProps}
      />
    </Box>
  );

  const chipEditor = (label, path, helper) => {
    const items = get(settings, path, []) || [];
    return (
      <Full>
        <Box sx={{ ...panelSx, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "baseline",
              justifyContent: "space-between",
              mb: 1,
              gap: 1,
            }}
          >
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {label}
              </Typography>
              {helper && (
                <Typography variant="caption" color="text.secondary">
                  {helper}
                </Typography>
              )}
            </Box>
            <Chip size="small" variant="outlined" label={`${items.length}`} />
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mb: 1.5, minHeight: 32 }}>
            {items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No items yet.
              </Typography>
            ) : (
              items.map((item) => (
                <Chip
                  key={item}
                  label={item}
                  onDelete={() => removeChip(path, item)}
                  size="small"
                  variant="outlined"
                />
              ))
            )}
          </Box>
          <TextField
            size="small"
            fullWidth
            placeholder={`Add ${label.toLowerCase()}…`}
            value={chipDraft[path] || ""}
            onChange={(e) =>
              setChipDraft((prev) => ({ ...prev, [path]: e.target.value }))
            }
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addChip(path);
              }
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Button size="small" startIcon={<Add />} onClick={() => addChip(path)}>
                    Add
                  </Button>
                </InputAdornment>
              ),
            }}
          />
        </Box>
      </Full>
    );
  };

  const toggleRow = (title, path, description) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 2,
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Box sx={{ minWidth: 0, pr: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {description}
        </Typography>
      </Box>
      <Switch
        checked={!!get(settings, path)}
        onChange={(e) => setField(path, e.target.checked)}
        edge="end"
        color="primary"
      />
    </Box>
  );

  const logoVal = get(settings, "company.logoUrl");
  const logoIsUrl = /^https?:\/\//i.test(logoVal);
  const terms = get(settings, "terms", []) || [];

  const PlaceholderChips = ({ items }) => (
    <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
      {items.map((p) => (
        <Tooltip key={p} title="Placeholder token">
          <Chip
            size="small"
            variant="outlined"
            label={p}
            sx={{ fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: 11 }}
          />
        </Tooltip>
      ))}
    </Stack>
  );

  const renderTab = () => {
    switch (activeTab.key) {
      case "company":
        return (
          <FormGrid>
            <Full>
              <Box
                sx={{
                  ...panelSx,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  background: `linear-gradient(135deg, ${alpha(
                    theme.palette.primary.main,
                    0.06
                  )}, transparent 60%)`,
                }}
              >
                <Avatar
                  variant="rounded"
                  src={logoIsUrl ? logoVal : undefined}
                  sx={{
                    width: 72,
                    height: 72,
                    bgcolor: "grey.100",
                    color: "text.secondary",
                    border: 1,
                    borderColor: "divider",
                  }}
                >
                  {!logoIsUrl && <ImageIcon />}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Company logo
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mb: 1.25 }}
                  >
                    Used on PDFs and branded pages. Upload replaces the stored key.
                  </Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<CloudUpload />}
                    size="small"
                  >
                    Upload logo
                    <input type="file" accept="image/*" hidden onChange={handleLogoUpload} />
                  </Button>
                </Box>
              </Box>
            </Full>
            {field("Company Name", "company.name")}
            {field("Legal Name", "company.legalName")}
            {field("Address", "company.address", {
              full: true,
              multiline: true,
              minRows: 2,
            })}
            {field("Support Contact", "company.supportContact", {
              helper: "Shown in emails when employees need help.",
            })}
            {field("Website", "company.website")}
            {field("Logo (S3 key or URL)", "company.logoUrl", {
              full: true,
              helper: "Prefer uploading above. Manual key/URL is also supported.",
            })}
          </FormGrid>
        );

      case "app":
        return (
          <FormGrid>
            {mailReadinessAlert}
            <Full>
              <Alert severity="info">
                Frontend URL builds activation, acknowledgment and verification links. Use your
                public HTTPS URL in production (no trailing slash). Branding and email templates
                are global for this deployment — Organizations scope users/assets only.
              </Alert>
            </Full>
            {field("App Name", "app.appName", {
              helper: "Appears in page titles and email subjects.",
            })}
            {field("Frontend URL", "app.frontendUrl", {
              placeholder: "https://assets.example.com",
            })}
          </FormGrid>
        );

      case "emails": {
        const base = `email.${selectedEmailTpl.key}`;
        return (
          <FormGrid>
            {mailReadinessAlert}
            <Full>
              <Box sx={panelSx}>
                <Section
                  title="Send test email"
                  description="Uses live mailer config (SMTP / Graph). Does not change templates."
                >
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                    <TextField
                      size="small"
                      label="Recipient"
                      value={testEmailTo}
                      onChange={(e) => setTestEmailTo(e.target.value)}
                      sx={{ minWidth: 240, flex: "1 1 220px" }}
                      placeholder="you@company.com"
                    />
                    <Button
                      variant="outlined"
                      disabled={testEmailSending || !testEmailTo.trim()}
                      onClick={sendTestEmail}
                    >
                      {testEmailSending ? "Sending…" : "Send test"}
                    </Button>
                  </Box>
                </Section>
              </Box>
            </Full>

            {field("Sender Name", "email.senderName", {
              full: true,
              helper: 'Display name in the From header, e.g. "Document IT".',
            })}

            <Full>
              <Alert severity="info" sx={{ py: 0.75 }}>
                <strong>Welcome</strong> goes out on user create. <strong>Instructions</strong> can
                be sent anytime from Inventory → By Employee or Users.
              </Alert>
            </Full>

            <Full>
              <Section title="Email templates" description="Pick a template, edit subject/body, preview, then Save.">
                <Tabs
                  value={emailTemplateKey}
                  onChange={(_, v) => setEmailTemplateKey(v)}
                  variant="scrollable"
                  scrollButtons="auto"
                  sx={{
                    minHeight: 40,
                    borderBottom: 1,
                    borderColor: "divider",
                    mb: 2,
                    "& .MuiTab-root": { minHeight: 40, textTransform: "none", fontWeight: 600 },
                  }}
                >
                  {EMAIL_TEMPLATES.map((tpl) => (
                    <Tab key={tpl.key} value={tpl.key} label={tpl.label} />
                  ))}
                </Tabs>

                <Box
                  sx={{
                    display: "grid",
                    gap: 2,
                    gridTemplateColumns: { xs: "1fr", lg: "220px 1fr" },
                  }}
                >
                  <Box sx={{ ...panelSx, p: 1.5, display: { xs: "none", lg: "block" } }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                      About this template
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {selectedEmailTpl.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedEmailTpl.hint}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: { xs: "block", lg: "none" }, mb: 1 }}
                    >
                      {selectedEmailTpl.hint}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                      Placeholders
                    </Typography>
                    <PlaceholderChips
                      items={[
                        "{{appName}}",
                        "{{companyName}}",
                        "{{empName}}",
                        "{{email}}",
                        "{{loginUrl}}",
                        "{{myDevicesUrl}}",
                        "{{setPasswordLink}}",
                        "{{deviceListHtml}}",
                        "{{supportContact}}",
                        "{{formUrl}}",
                      ]}
                    />
                    <Box
                      sx={{
                        display: "grid",
                        gap: 2,
                        mt: 2,
                        gridTemplateColumns: "1fr",
                      }}
                    >
                      {field("Subject", `${base}.subject`, { full: true })}
                      {field("HTML Body", `${base}.html`, {
                        full: true,
                        multiline: true,
                        minRows: 10,
                      })}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        <Button
                          size="small"
                          startIcon={<Visibility />}
                          variant="outlined"
                          onClick={() => setPreviewEmail(selectedEmailTpl.key)}
                        >
                          Preview
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => loadRecommendedEmailTemplate(selectedEmailTpl.key)}
                        >
                          Load recommended
                        </Button>
                      </Stack>
                    </Box>
                  </Box>
                </Box>
              </Section>
            </Full>
          </FormGrid>
        );
      }

      case "documents":
        return (
          <FormGrid>
            <Section title="PDF layout" description="Header title and footer lines printed on undertaking PDFs." />
            {field("PDF Title", "pdf.title", { full: true })}
            {field("PDF Footer Name", "pdf.footerName")}
            {field("PDF Footer Address", "pdf.footerAddress")}

            <Section
              title="Terms & Conditions"
              description="One clause per line. Numbered automatically on the form and PDF."
              action={
                <Button size="small" variant="outlined" onClick={loadRecommendedTerms}>
                  Restore recommended
                </Button>
              }
            >
              <Box
                sx={{
                  display: "grid",
                  gap: 2,
                  gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                }}
              >
                <TextField
                  label="Terms (edit)"
                  value={termsDraft}
                  onChange={(e) => commitTermsDraft(e.target.value)}
                  fullWidth
                  multiline
                  minRows={14}
                  size="small"
                  helperText={`${terms.length} clause(s)`}
                />
                <Box
                  sx={{
                    ...panelSx,
                    height: "100%",
                    maxHeight: 420,
                    overflow: "auto",
                    bgcolor: alpha(theme.palette.grey[500], 0.06),
                  }}
                >
                  <Typography variant="overline" color="text.secondary">
                    Live preview
                  </Typography>
                  {terms.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      No terms yet.
                    </Typography>
                  ) : (
                    <Box component="ol" sx={{ pl: 2.5, m: 0, mt: 1 }}>
                      {terms.map((line, i) => (
                        <Typography
                          component="li"
                          key={`${i}-${line.slice(0, 24)}`}
                          variant="body2"
                          sx={{ mb: 1, lineHeight: 1.55 }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            </Section>
          </FormGrid>
        );

      case "form":
        return (
          <FormGrid>
            <Full>
              <Alert severity="info" sx={{ py: 0.75 }}>
                These lists power undertaking form dropdowns. Press Enter to add an item.
              </Alert>
            </Full>
            {chipEditor("Manufacturers", "formOptions.manufacturers", "e.g. Dell, HP, Lenovo")}
            {chipEditor(
              "Accessories",
              "formOptions.accessories",
              "Typical kit: Mouse, Headphones, External Monitor, Monitor Cable (+ Charger, Bag, Keyboard)"
            )}
            {chipEditor(
              "Specifications",
              "formOptions.specifications",
              "e.g. i5, 16GB, 512GB SSD"
            )}
          </FormGrid>
        );

      case "locations": {
        const sites = get(settings, "locations.sites", []) || [];
        const setSites = (next) => setField("locations.sites", next);
        return (
          <FormGrid>
            <Full>
              <Alert severity="info" sx={{ py: 0.75 }}>
                Cascading Site → Building → Floor choices on Asset Inventory. Asset forms also
                allow free-text “Other”.
              </Alert>
            </Full>
            {sites.map((site, si) => (
              <Full key={`site-${si}`}>
                <Box sx={{ ...panelSx, display: "flex", flexDirection: "column", gap: 1.5 }}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip size="small" label={`Site ${si + 1}`} variant="outlined" />
                    <TextField
                      size="small"
                      label="Site name"
                      fullWidth
                      value={site.name || ""}
                      onChange={(e) => {
                        const next = [...sites];
                        next[si] = { ...next[si], name: e.target.value };
                        setSites(next);
                      }}
                    />
                    <Button
                      color="error"
                      size="small"
                      onClick={() => setSites(sites.filter((_, i) => i !== si))}
                    >
                      Remove
                    </Button>
                  </Stack>
                  {(site.buildings || []).map((b, bi) => (
                    <Box
                      key={`b-${bi}`}
                      sx={{
                        pl: 1.5,
                        ml: 0.5,
                        borderLeft: "3px solid",
                        borderColor: alpha(theme.palette.primary.main, 0.35),
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      <Stack direction="row" spacing={1} alignItems="center">
                        <TextField
                          size="small"
                          label="Building"
                          fullWidth
                          value={b.name || ""}
                          onChange={(e) => {
                            const next = [...sites];
                            const buildings = [...(next[si].buildings || [])];
                            buildings[bi] = { ...buildings[bi], name: e.target.value };
                            next[si] = { ...next[si], buildings };
                            setSites(next);
                          }}
                        />
                        <Button
                          size="small"
                          color="error"
                          onClick={() => {
                            const next = [...sites];
                            next[si] = {
                              ...next[si],
                              buildings: (next[si].buildings || []).filter((_, i) => i !== bi),
                            };
                            setSites(next);
                          }}
                        >
                          Remove
                        </Button>
                      </Stack>
                      <TextField
                        size="small"
                        fullWidth
                        label="Floors (comma-separated)"
                        value={(b.floors || []).join(", ")}
                        helperText="Example: Ground, 1, 2, Mezzanine"
                        onChange={(e) => {
                          const floors = e.target.value
                            .split(",")
                            .map((x) => x.trim())
                            .filter(Boolean);
                          const next = [...sites];
                          const buildings = [...(next[si].buildings || [])];
                          buildings[bi] = { ...buildings[bi], floors };
                          next[si] = { ...next[si], buildings };
                          setSites(next);
                        }}
                      />
                    </Box>
                  ))}
                  <Button
                    size="small"
                    startIcon={<Add />}
                    onClick={() => {
                      const next = [...sites];
                      next[si] = {
                        ...next[si],
                        buildings: [...(next[si].buildings || []), { name: "", floors: [] }],
                      };
                      setSites(next);
                    }}
                  >
                    Add building
                  </Button>
                </Box>
              </Full>
            ))}
            <Full>
              <Button
                variant="outlined"
                startIcon={<Add />}
                onClick={() =>
                  setSites([...sites, { name: "", buildings: [{ name: "", floors: [] }] }])
                }
              >
                Add site
              </Button>
            </Full>
          </FormGrid>
        );
      }

      case "assets":
        return (
          <FormGrid>
            <Full>
              <Alert severity="info" sx={{ py: 0.75 }}>
                Soft-removed assets stay in the Recycle Bin until retention expires, then are purged
                permanently.
              </Alert>
            </Full>
            {field("Recycle bin retention (days)", "assets.recycleBinRetentionDays", {
              number: true,
              helper: "Days a removed asset remains restorable.",
            })}
            <Section
              title="Warranty & EOL alerts"
              description="Daily scan (09:00). Emails + Slack/Teams (if configured) + warranty.expiring webhooks."
            >
              <Box sx={panelSx}>
                {toggleRow(
                  "Enable warranty alerts",
                  "warrantyAlerts.enabled",
                  "Notify when warranty is expired or within the thresholds below."
                )}
              </Box>
            </Section>
            <Box sx={{ gridColumn: "1 / -1", minWidth: 0 }}>
              <TextField
                label="Threshold days (comma-separated)"
                value={(get(settings, "warrantyAlerts.thresholdsDays", []) || []).join(", ")}
                onChange={(e) => {
                  const nums = e.target.value
                    .split(",")
                    .map((x) => Number(String(x).trim()))
                    .filter((n) => !Number.isNaN(n) && n > 0);
                  setField("warrantyAlerts.thresholdsDays", nums);
                }}
                fullWidth
                size="small"
                helperText="e.g. 30, 60, 90. Each asset is counted in the nearest (smallest) bucket."
              />
            </Box>
            {chipEditor(
              "Warranty alert emails",
              "warrantyAlerts.notifyEmails",
              "Also uses Notifications → Admin emails when set."
            )}
            <Full>
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <Button
                  variant="outlined"
                  size="small"
                  disabled={warrantyRunning}
                  onClick={async () => {
                    setWarrantyRunning(true);
                    try {
                      const r = await axios.post("/assets/warranty-alerts/run");
                      const s = r.data.result?.summary;
                      toast.success(
                        r.data.result?.notified
                          ? `Alerted on ${s?.totalFlagged || 0} asset(s)`
                          : `Scan done (${s?.totalFlagged || 0} flagged, notify=${!!r.data.result?.notified})`
                      );
                    } catch (e) {
                      toast.error(e.response?.data?.error || "Warranty run failed");
                    } finally {
                      setWarrantyRunning(false);
                    }
                  }}
                >
                  {warrantyRunning ? "Running…" : "Run warranty scan now"}
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Last run is deduped per day unless the asset set changes.
                </Typography>
              </Stack>
            </Full>
          </FormGrid>
        );

      case "auth":
        return (
          <FormGrid>
            <Full>
              <Alert severity="info" sx={{ py: 0.75 }}>
                SSO go-live checklist: register the redirect URIs in Azure/Google, set Client
                ID/secret in backend <code>.env</code>, then enable providers below. Password
                login stays available unless <strong>SSO-only for admins</strong> is turned on.
                Admin accounts are never JIT-created.
              </Alert>
            </Full>
            {ssoStatus?.checklist?.length > 0 && (
              <Full>
                <Box sx={panelSx}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mb: 1.5 }}
                    flexWrap="wrap"
                    useFlexGap
                    spacing={1}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Go-live checklist ({ssoStatus.readyCount}/{ssoStatus.totalCount})
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() =>
                        axios
                          .get("/auth/providers")
                          .then((r) => setSsoStatus(r.data.result || null))
                      }
                    >
                      Refresh status
                    </Button>
                  </Stack>
                  <Stack spacing={1}>
                    {ssoStatus.checklist.map((row) => (
                      <Box
                        key={row.id}
                        sx={{
                          display: "flex",
                          gap: 1,
                          alignItems: "flex-start",
                          py: 0.75,
                          borderBottom: "1px solid",
                          borderColor: "divider",
                          "&:last-of-type": { borderBottom: 0 },
                        }}
                      >
                        {row.ok ? (
                          <CheckCircleOutline color="success" fontSize="small" sx={{ mt: 0.2 }} />
                        ) : (
                          <HighlightOff color="warning" fontSize="small" sx={{ mt: 0.2 }} />
                        )}
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {row.label}
                          </Typography>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", wordBreak: "break-all" }}
                          >
                            {row.detail}
                          </Typography>
                        </Box>
                        {String(row.detail || "").includes("/auth/") && (
                          <Button
                            size="small"
                            startIcon={<ContentCopy />}
                            onClick={() => {
                              navigator.clipboard?.writeText(row.detail);
                              toast.info("Redirect URI copied");
                            }}
                          >
                            Copy
                          </Button>
                        )}
                      </Box>
                    ))}
                  </Stack>
                  {ssoStatus.redirectUris && (
                    <Alert severity="success" sx={{ mt: 1.5, py: 0.5 }}>
                      Azure redirect URI: <code>{ssoStatus.redirectUris.microsoft}</code>
                      <br />
                      Google redirect URI: <code>{ssoStatus.redirectUris.google}</code>
                    </Alert>
                  )}
                </Box>
              </Full>
            )}
            <Full>
              <Box sx={panelSx}>
                {toggleRow(
                  "Microsoft Entra ID",
                  "auth.microsoftEnabled",
                  "Show “Continue with Microsoft” on the login page when secrets are configured."
                )}
                {toggleRow(
                  "Google Workspace",
                  "auth.googleEnabled",
                  "Show “Continue with Google” on the login page when secrets are configured."
                )}
                {toggleRow(
                  "Just-in-time provisioning",
                  "auth.jitProvisioning",
                  "If the SSO email is not in Users yet, create an Active account automatically."
                )}
                {toggleRow(
                  "SSO-only for admins",
                  "auth.ssoOnlyAdmins",
                  "Block password login for Admin / NetworkOwner / CompanyAdmin. They must use Microsoft or Google SSO."
                )}
                {!!get(settings, "auth.ssoOnlyAdmins") &&
                  !get(settings, "auth.microsoftEnabled") &&
                  !get(settings, "auth.googleEnabled") && (
                    <Alert severity="warning" sx={{ mt: 1.5, py: 0.5 }}>
                      SSO-only is on, but no SSO provider is enabled. Admins may be locked out of
                      password login with no SSO alternative.
                    </Alert>
                  )}
              </Box>
            </Full>
            {field("JIT default role", "auth.jitDefaultRole", {
              helper: "Employee, Auditor, Manager, Guest, … (Admin is never auto-assigned).",
            })}
          </FormGrid>
        );

      case "reminders":
        return (
          <FormGrid>
            <Full>
              <Box sx={panelSx}>
                {toggleRow(
                  "Enable invite reminders",
                  "reminders.enabled",
                  "When on, the hourly cron re-sends pending public undertaking invites."
                )}
              </Box>
            </Full>
            {field("Stale after (days)", "reminders.staleAfterDays", {
              number: true,
              helper: "Invite must be this many days old before a reminder is sent.",
            })}
            {field("Max resends", "reminders.maxResends", {
              number: true,
              helper: "Stop reminding after this many follow-ups.",
            })}
          </FormGrid>
        );

      case "notifications":
        return (
          <FormGrid>
            <Full>
              <Box sx={panelSx}>
                {toggleRow(
                  "Notify admins on submission",
                  "notifications.notifyAdminOnSubmission",
                  "Email the addresses below whenever a new undertaking is submitted."
                )}
              </Box>
            </Full>
            {chipEditor(
              "Admin emails",
              "notifications.adminEmails",
              "One email per chip. Used only when the toggle above is on."
            )}
          </FormGrid>
        );

      case "verification":
        return (
          <FormGrid>
            <Section title="Schedule" description="Master switches and cadence for verification campaigns.">
              <Box sx={panelSx}>
                {toggleRow(
                  "Enable reminders & scheduling",
                  "verification.enabled",
                  "Master switch for campaign reminders and overdue marking."
                )}
                {toggleRow(
                  "Auto-send yearly",
                  "verification.autoSend",
                  "Automatically start a campaign on the send date below."
                )}
              </Box>
            </Section>
            {field("Annual send date (MM-DD)", "verification.sendOn", {
              placeholder: "01-01",
              helper: "Calendar day the yearly campaign starts.",
            })}
            {field("Due in (days)", "verification.dueInDays", {
              number: true,
              helper: "Days from send until acknowledgment is due.",
            })}
            {field("Reminder every (days)", "verification.reminderEveryDays", {
              number: true,
            })}
            {field("Max reminders", "verification.maxReminders", {
              number: true,
            })}

            <Section
              title="Campaign email"
              description="Message employees receive for verification links."
              action={
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => setPreviewEmail("verification")}
                  >
                    Preview
                  </Button>
                  <Button size="small" variant="outlined" onClick={loadRecommendedVerificationTemplate}>
                    Load recommended
                  </Button>
                </Stack>
              }
            >
              <PlaceholderChips
                items={[
                  "{{appName}}",
                  "{{companyName}}",
                  "{{empName}}",
                  "{{assetLabel}}",
                  "{{dueDate}}",
                  "{{link}}",
                  "{{supportContact}}",
                ]}
              />
              <Box sx={{ display: "grid", gap: 2, mt: 2, gridTemplateColumns: "1fr" }}>
                {field("Subject", "verification.template.subject", { full: true })}
                {field("HTML Body", "verification.template.html", {
                  full: true,
                  multiline: true,
                  minRows: 8,
                })}
              </Box>
            </Section>
          </FormGrid>
        );

      default:
        return null;
    }
  };

  const previewHtml =
    previewEmail === "verification"
      ? get(settings, "verification.template.html")
      : previewEmail
        ? get(settings, `email.${previewEmail}.html`)
        : "";
  const previewSubject =
    previewEmail === "verification"
      ? get(settings, "verification.template.subject")
      : previewEmail
        ? get(settings, `email.${previewEmail}.subject`)
        : "";

  const navContent = (
    <Box>
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search settings…"
          value={navQuery}
          onChange={(e) => setNavQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      {NAV_GROUPS.map((group) => {
        const items = filteredTabs.filter((t) => t.group === group);
        if (!items.length) return null;
        return (
          <List
            key={group}
            dense
            disablePadding
            subheader={
              <ListSubheader
                disableSticky
                sx={{
                  bgcolor: "transparent",
                  lineHeight: 2.2,
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  color: "text.secondary",
                  px: 1.5,
                }}
              >
                {group}
              </ListSubheader>
            }
            sx={{ mb: 1 }}
          >
            {items.map((t) => (
              <ListItemButton
                key={t.key}
                selected={tabKey === t.key}
                onClick={() => selectTab(t.key)}
                sx={{
                  borderRadius: 1.5,
                  mx: 0.75,
                  mb: 0.25,
                  gap: 1,
                  alignItems: "flex-start",
                  py: 1,
                  "&.Mui-selected": {
                    bgcolor: alpha(theme.palette.primary.main, 0.1),
                    color: "primary.main",
                    "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.14) },
                  },
                }}
              >
                <Box sx={{ display: "flex", color: "inherit", opacity: 0.9, mt: 0.15 }}>
                  {t.icon}
                </Box>
                <ListItemText
                  primary={t.label}
                  secondary={t.desc}
                  primaryTypographyProps={{ variant: "body2", fontWeight: 600 }}
                  secondaryTypographyProps={{
                    variant: "caption",
                    sx: {
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      mt: 0.15,
                    },
                  }}
                />
              </ListItemButton>
            ))}
          </List>
        );
      })}
      {filteredTabs.length === 0 && (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1 }}>
          No matching sections.
        </Typography>
      )}
    </Box>
  );

  return (
    <AdminLayout
      title="Settings"
      subtitle="Organization, communications and content"
      onLogout={handleLogout}
    >
      <Container maxWidth="lg" sx={{ pb: 12 }}>
        {isMobile && (
          <Paper
            elevation={0}
            sx={{
              mb: 2,
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
            }}
          >
            <Tabs
              value={tabKey}
              onChange={(_, v) => selectTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                minHeight: 48,
                "& .MuiTab-root": {
                  minHeight: 48,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 13,
                },
              }}
            >
              {TABS.map((t) => (
                <Tab
                  key={t.key}
                  value={t.key}
                  icon={t.icon}
                  iconPosition="start"
                  label={t.label}
                />
              ))}
            </Tabs>
          </Paper>
        )}

        <Box
          sx={{
            display: "grid",
            gap: 2.5,
            gridTemplateColumns: { xs: "1fr", md: "280px minmax(0, 1fr)" },
            alignItems: "start",
          }}
        >
          {!isMobile && (
            <Paper
              elevation={0}
              sx={{
                borderRadius: 2,
                border: 1,
                borderColor: "divider",
                py: 0.5,
                position: "sticky",
                top: 80,
                maxHeight: "calc(100vh - 104px)",
                overflow: "auto",
              }}
            >
              {navContent}
            </Paper>
          )}

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 3 },
              borderRadius: 2,
              border: 1,
              borderColor: "divider",
              minHeight: 440,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, mb: 2 }}>
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {activeTab.icon}
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.25 }}>
                    {activeTab.label}
                  </Typography>
                  <Chip size="small" label={activeTab.group} variant="outlined" />
                  {dirty && <Chip size="small" color="warning" label="Unsaved changes" />}
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  {activeTab.desc}
                </Typography>
              </Box>
            </Box>
            <Divider sx={{ mb: 2.5 }} />
            {renderTab()}
          </Paper>
        </Box>
      </Container>

      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          left: { xs: 12, md: "auto" },
          right: 12,
          bottom: 12,
          zIndex: 20,
          px: 2,
          py: 1.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 1.5,
          borderRadius: 3,
          border: 1,
          borderColor: dirty ? "warning.light" : "divider",
          bgcolor: "background.paper",
          maxWidth: { md: 520 },
          ml: { md: "auto" },
          boxShadow: dirty
            ? `0 8px 28px ${alpha(theme.palette.warning.main, 0.25)}`
            : undefined,
        }}
      >
        {dirty ? (
          <Chip size="small" color="warning" label="Unsaved" sx={{ mr: "auto" }} />
        ) : (
          <Chip size="small" variant="outlined" label="All saved" sx={{ mr: "auto" }} />
        )}
        <Button
          variant="text"
          startIcon={<RestartAlt />}
          onClick={discardChanges}
          disabled={!dirty || saving}
        >
          Discard
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveIcon />}
          onClick={handleSave}
          disabled={saving || !dirty}
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </Paper>

      <Dialog
        open={Boolean(previewEmail)}
        onClose={() => setPreviewEmail(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          Email preview
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontWeight: 400 }}>
            {previewSubject || "(no subject)"}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box
            sx={{ bgcolor: "#fff", minHeight: 120 }}
            dangerouslySetInnerHTML={{
              __html: previewHtml || "<p><em>Empty template</em></p>",
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewEmail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <ToastContainer position="top-center" autoClose={2500} />
    </AdminLayout>
  );
}

export default SettingsPage;
